import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, Star } from "lucide-react";
import api from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { PrimaryLink } from "@/components/public/ui";
import useSeo from "@/lib/useSeo";

export default function SolutionDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/solutions/${slug}`).then(({ data }) => setS(data)).catch(() => setS(false)); }, [slug]);
  useSeo({ title: s?.seo_title || s?.name, description: s?.seo_description || s?.short_desc, image: s?.image });

  if (s === null) return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-slate-400">Chargement…</div>;
  if (s === false) return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-slate-500">Solution introuvable. <Link to="/solutions" className="text-[#0F2A6B] underline">Retour</Link></div>;

  return (
    <div>
      <section className="bg-gradient-to-br from-[#0F2A6B] to-[#0A1F52] relative grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 relative grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link to="/solutions" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-6"><ArrowLeft className="w-4 h-4" /> Toutes les solutions</Link>
            <div className="text-xs font-mono text-[#4CB944] uppercase tracking-wider">{s.category}</div>
            <h1 className="mt-2 text-4xl sm:text-5xl font-bold text-white font-head">{s.name}</h1>
            <p className="mt-4 text-lg text-slate-300">{s.short_desc}</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <PrimaryLink to="/devis" testid="solution-devis-button" className="bg-[#4CB944] hover:bg-[#3AA033]">Demander un devis</PrimaryLink>
              <button onClick={() => openWhatsApp({ page: "solution", solution: s.name, message: `Bonjour, je souhaite une démonstration de ${s.name}.` })}
                className="inline-flex items-center px-6 py-3 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10">Demander une démonstration</button>
            </div>
          </div>
          {s.image && <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl"><img src={s.image} alt={s.name} className="w-full h-72 object-cover" /></div>}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <div><h2 className="text-2xl font-bold text-slate-900 font-head mb-3">Présentation</h2><p className="text-slate-600 leading-relaxed whitespace-pre-line">{s.description}</p></div>
          {s.problem && <div className="p-6 rounded-2xl bg-[#4CB944]/5 border border-[#4CB944]/20"><h3 className="font-semibold text-slate-900 mb-2">Problème résolu</h3><p className="text-slate-600">{s.problem}</p></div>}
          {Array.isArray(s.features) && s.features.length > 0 && (
            <div><h2 className="text-2xl font-bold text-slate-900 font-head mb-4">Fonctionnalités</h2>
              <div className="grid sm:grid-cols-2 gap-3">{s.features.map((f) => (<div key={f} className="flex items-center gap-2 text-slate-700"><Check className="w-5 h-5 text-[#4CB944] shrink-0" />{f}</div>))}</div></div>
          )}
          {Array.isArray(s.benefits) && s.benefits.length > 0 && (
            <div><h2 className="text-2xl font-bold text-slate-900 font-head mb-4">Avantages</h2>
              <div className="space-y-2">{s.benefits.map((b) => (<div key={b} className="flex items-center gap-2 text-slate-700"><Star className="w-4 h-4 fill-[#4CB944] text-[#4CB944] shrink-0" />{b}</div>))}</div></div>
          )}
        </div>
        <aside className="space-y-6">
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm sticky top-24">
            <div className="text-sm text-slate-500">Tarification</div>
            <div className="text-2xl font-bold text-[#0F2A6B] mt-1">{s.price || "Sur devis"}</div>
            {s.pricing_model && <div className="text-sm text-slate-500 mt-1">{s.pricing_model}</div>}
            {s.target && <div className="mt-4 pt-4 border-t border-slate-100"><div className="text-xs text-slate-400 uppercase font-mono">Public cible</div><div className="text-sm text-slate-700 mt-1">{s.target}</div></div>}
            <PrimaryLink to="/devis" testid="solution-sidebar-devis" className="w-full mt-5">Obtenir un devis</PrimaryLink>
          </div>
        </aside>
      </section>
    </div>
  );
}
